/* Find My Pump — drumpump.com
 * Engine (pure functions, testable in node) + a small UI. Needs pump-engine.js, loaded first.
 * Data: products from /collections/<handle>?view=fmp-json (metafields only), questions and scoring from fmp-rules.json,
 * chemical ratings from pump-chemicals.json.
 * Safety rules (do not relax): outside the chemical question, a pump must carry the fluid tag its manufacturer states
 * (custom.fluid_tags); for a named chemical, PumpEngine decides from the manufacturer's chart (every brand with a chart),
 * including flammability, conditions and bundled hoses. Anything unmatched goes to the contact form. Never infer
 * compatibility from materials or titles.
 */
(function () {
  'use strict';
  var inNode = typeof module !== 'undefined' && module.exports;
  var PE = inNode ? require('./pump-engine.js') : window.PumpEngine;

  // ---------------------------------------------------------------- engine
  var engine = {};
  var NOT_LISTED = '__not_listed__';

  function arr(v) { return Array.isArray(v) ? v : []; }
  function optionById(q, id) { return (q.options || []).concat(q.grease_options || []).filter(function (o) { return o.id === id; })[0]; }
  function chemicalBranch(a) { return a.fluid === 'chemical' || a.fluid === 'unsure'; }

  engine.lookupChemical = function (chems, id) { return id && id !== NOT_LISTED ? PE.byId(chems, id) : null; };

  engine.fluidTag = function (rules, a) {
    if (a.fluid === 'fuel') return rules.fluid_to_tag['fuel:' + a.fuel_type];
    return rules.fluid_to_tag[a.fluid];
  };

  engine.thicknessAsked = function (rules, a) {
    return rules.questions.thickness.skip_if.fluid.indexOf(a.fluid) < 0;
  };

  engine.needsContact = function (rules, chems, a) {
    if (rules.contact_branches.indexOf(a.fluid) >= 0) return 'branch';
    if (chemicalBranch(a) && !engine.lookupChemical(chems, a.chemical)) return 'chemical';
    return null;
  };

  // Outside the chemical question: a hose sold with the pump must suit the branch's liquid
  engine.hoseOk = function (p, rules, a) {
    var hose = PE.bundledHose(p);
    if (!hose) return true;
    var allowed = rules.hose_rules.by_branch[a.fluid === 'fuel' ? 'fuel:' + a.fuel_type : a.fluid] || [];
    return allowed.indexOf(hose) >= 0;
  };

  // The container, thickness and power answers, one product at a time (an unanswered or skipped question passes)
  engine.containerOk = function (p, rules, a) {
    var copt = optionById(rules.questions.container, a.container), cont = arr(p.containers);
    return !!copt && copt.tags.some(function (t) { return cont.indexOf(t) >= 0; });
  };
  engine.thicknessOk = function (p, rules, a) {
    if (!engine.thicknessAsked(rules, a) || !a.thickness) return true;
    var topt = optionById(rules.questions.thickness, a.thickness);
    return !!topt && arr(p.viscosity).indexOf(topt['class']) >= 0;
  };
  engine.powerOk = function (p, rules, a) {
    if (!a.power) return true;
    var popt = optionById(rules.questions.power, a.power);
    return !(popt && popt.power.length && popt.power.indexOf(p.power) < 0);
  };

  // true when the pump fits every answer; 'upgrade' when it fits once a required upgrade part is added; 'check' when its
  // manufacturer's rating for the chemical comes with a condition no part meets (never recommended); false otherwise.
  // opts.catalog: PumpEngine.partsCatalog(products); opts.sources limits the charts used (tests only).
  engine.match = function (p, rules, data, chem, a, opts) {
    if (!PE.usable(p, data)) return false;
    var tags = arr(p.fluid_tags), rating = null;
    if (chemicalBranch(a)) {
      if (opts && opts.sources && opts.sources.indexOf(p.rating_source) < 0) return false;
      rating = PE.rate(p, chem, data, a.flammable, opts && opts.catalog);
      if (!rating) return false;
    } else {
      if (!engine.hoseOk(p, rules, a)) return false;
      var tag = engine.fluidTag(rules, a);
      if (!tag || tags.indexOf(tag) < 0) return false;
    }
    if (!engine.containerOk(p, rules, a) || !engine.thicknessOk(p, rules, a) || !engine.powerOk(p, rules, a)) return false;
    var food = arr(a.extras).indexOf('foodgrade') >= 0;
    if (food && tags.indexOf('food-grade') < 0) return false;
    // A required upgrade replaces parts the liquid touches, so a food-grade pump that needs one is a question for us
    if (food && rating && rating.status === 'upgrade') return 'check';
    return rating && rating.status !== 'rated' ? rating.status : true;
  };
  engine.passes = function (p, rules, data, chem, a, opts) { return engine.match(p, rules, data, chem, a, opts) === true; };

  function featureBonus(p, table) {
    var f = arr(p.features), s = 0;
    Object.keys(table || {}).forEach(function (k) { if (k.charAt(0) !== '_' && f.indexOf(k) >= 0) s += table[k]; });
    return s;
  }

  // Points for a pump type only under certain answers, e.g. a rotary for oil used occasionally. Each rule:
  // { pump_type, points, when: { question id: [answer ids] } }; every listed question must match.
  function answerBonus(p, list, a) {
    var s = 0;
    arr(list).forEach(function (r) {
      if (!r || r.pump_type !== p.pump_type || typeof r.points !== 'number') return;
      var w = r.when || {};
      if (Object.keys(w).every(function (q) { return arr(w[q]).indexOf(a[q]) >= 0; })) s += r.points;
    });
    return s;
  }

  engine.score = function (p, rules, data, chem, a) {
    var sc = rules.scoring, extras = arr(a.extras), fb = sc.feature_bonus || {};
    var vp = sc.volume_power[a.volume || 'occasional'] || {};
    var s = vp[p.power] || 0;
    if (p.power !== 'manual' && p.role === 'package') s += sc.powered_package_bonus;
    if (!chemicalBranch(a) && arr(p.fluid_tags)[0] === engine.fluidTag(rules, a)) s += sc.primary_fluid_bonus || 0;
    var tb = sc.pump_type_bonus || {};
    if (p.pump_type && p.pump_type.charAt(0) !== '_' && typeof tb[p.pump_type] === 'number') s += tb[p.pump_type];
    s += answerBonus(p, sc.answer_bonus, a);
    s += featureBonus(p, extras.indexOf('metered') >= 0 ? fb.metered_chosen : fb.metered_not_chosen);
    if (extras.indexOf('nondrip') >= 0) s += featureBonus(p, fb.nondrip_chosen);
    if (chem) {
      var r = PE.rate(p, chem, data, a.flammable);
      if (r) s += (sc.chemical_rating_bonus_by_rank || {})[String(r.rank)] || 0;
      // a liquid that needs grounding is recommended with a pump that arrives ready to ground, where one fits
      if (PE.needsGroundable(chem, a.flammable) && PE.groundReady(p)) s += sc.ready_to_ground_bonus || 0;
    }
    return s;
  };

  // Pumps rated as sold come first; only when none fits does a pump that needs a required upgrade become the recommendation.
  engine.evaluate = function (products, rules, data, a, opts) {
    var chems = PE.chemicals(data);
    var contact = engine.needsContact(rules, chems, a);
    if (contact) return { status: 'contact', reason: contact };
    var chem = chemicalBranch(a) ? engine.lookupChemical(chems, a.chemical) : null;
    var o = Object.assign({}, opts || {}, { catalog: PE.partsCatalog(products) });
    var rated = [], withUpgrade = [], checks = [];
    products.forEach(function (p) {
      var m = engine.match(p, rules, data, chem, a, o);
      if (m === true) rated.push({ p: p, s: engine.score(p, rules, data, chem, a) });
      else if (m === 'upgrade') withUpgrade.push({ p: p, s: engine.score(p, rules, data, chem, a) });
      else if (m === 'check') PE.rate(p, chem, data, a.flammable, o.catalog).reasons.forEach(function (t) {
        var line = PE.sourceLabels(data, [p.rating_source])[0] + ': ' + t;
        if (checks.indexOf(line) < 0) checks.push(line);
      });
    });
    var upgrade = !rated.length && withUpgrade.length > 0, scored = upgrade ? withUpgrade : rated;
    if (!scored.length) {
      // a manufacturer's "do not use" for this liquid is worth saying on the contact screen too
      PE.notes(chem, null, ['do-not-use']).forEach(function (n) { var line = PE.sourceLabels(data, [n.s])[0] + ': ' + n.t; if (checks.indexOf(line) < 0) checks.unshift(line); });
      return { status: 'none', checks: checks };
    }
    scored.sort(function (x, y) {
      if (y.s !== x.s) return y.s - x.s;
      if (x.p.price !== y.p.price) return x.p.price - y.p.price;
      var mx = x.p.power === 'manual' ? 0 : 1, my = y.p.power === 'manual' ? 0 : 1;
      if (mx !== my) return mx - my;
      return (x.p.title || '').localeCompare(y.p.title || '');
    });
    var seen = {}, families = [];
    scored.forEach(function (e) {
      var f = e.p.family || e.p.handle;
      if (!seen[f]) { seen[f] = true; families.push(e); }
    });
    var picked = families.slice(0, 1 + rules.scoring.alternates), upgrades = {};
    if (upgrade) picked.forEach(function (e) {
      var r = PE.rate(e.p, chem, data, a.flammable, o.catalog);
      upgrades[e.p.handle] = { parts: r.parts.map(function (id) { return PE.part(data, o.catalog, id); }), reasons: r.reasons.map(function (t) { return r.label + ': ' + t; }) };
    });
    return {
      status: 'ok',
      result: families[0].p,
      score: families[0].s,
      alternates: picked.slice(1).map(function (e) { return e.p; }),
      matched: scored.length,
      upgrade: upgrade,
      upgrades: upgrades
    };
  };

  // Which question comes next, given current answers. Returns null when finished.
  engine.nextStep = function (rules, data, a) {
    var q = rules.questions;
    if (!a.fluid) return 'fluid';
    if (rules.contact_branches.indexOf(a.fluid) >= 0) return null;
    if (a.fluid === 'fuel' && !a.fuel_type) return 'fuel_type';
    if (chemicalBranch(a)) {
      if (!a.chemical) return 'chemical';
      var chem = engine.lookupChemical(PE.chemicals(data), a.chemical);
      if (!chem) return null;
      if (PE.flammabilityUnknown(chem) && !a.flammable) return 'flammable';
    }
    if (!a.container) return 'container';
    if (engine.thicknessAsked(rules, a) && !a.thickness) return 'thickness';
    if (!a.volume) return 'volume';
    if (q.power.show_if.volume.indexOf(a.volume) >= 0 && !a.power) return 'power';
    if (!a.extras) return 'extras';
    return null;
  };

  // The answers a question shows, given the answers before it (grease has its own containers; some answers show for one fluid)
  engine.optionsFor = function (rules, a, step) {
    var q = rules.questions[step];
    var options = (step === 'container' && a.fluid === 'grease') ? q.grease_options : q.options;
    return arr(options).filter(function (o) { return !o.show_if || arr(o.show_if.fluid).indexOf(a.fluid) >= 0; });
  };

  // Can this product still be the answer? It must fit the answers given, and every question still to come must have an
  // answer it fits (volume never filters, so the one that skips the power question is used; no extras).
  engine.completes = function (p, rules, data, chem, a, opts) {
    var q = rules.questions, c = Object.assign({}, a);
    function pick(step, fits) {
      var o = engine.optionsFor(rules, c, step).filter(function (x) { var t = Object.assign({}, c); t[step] = x.id; return fits(p, rules, t); })[0];
      if (o) c[step] = o.id;
      return !!o;
    }
    if (!c.container && !pick('container', engine.containerOk)) return false;
    if (engine.thicknessAsked(rules, c) && !c.thickness && !pick('thickness', engine.thicknessOk)) return false;
    if (!c.volume) {
      var vols = engine.optionsFor(rules, c, 'volume');
      c.volume = (vols.filter(function (x) { return q.power.show_if.volume.indexOf(x.id) < 0; })[0] || vols[0]).id;
    }
    if (q.power.show_if.volume.indexOf(c.volume) >= 0 && !c.power && !pick('power', engine.powerOk)) return false;
    if (!c.extras) c.extras = ['none'];
    var m = engine.match(p, rules, data, chem, c, opts);
    return m === true || m === 'upgrade';
  };

  // Is there still a way from these answers to a recommended pump? Branches that go to the contact form by design, and
  // answers that depend on which chemical the customer names, count as yes.
  engine.reachable = function (products, rules, data, a, opts) {
    var step = engine.nextStep(rules, data, a), chems = PE.chemicals(data);
    if (step === 'chemical' || (!step && engine.needsContact(rules, chems, a))) return true;
    if (step === 'fluid' || step === 'fuel_type' || step === 'flammable') {
      return engine.optionsFor(rules, a, step).some(function (x) { var b = Object.assign({}, a); b[step] = x.id; return engine.reachable(products, rules, data, b, opts); });
    }
    var chem = chemicalBranch(a) ? engine.lookupChemical(chems, a.chemical) : null;
    var o = Object.assign({}, opts || {}, { catalog: (opts && opts.catalog) || PE.partsCatalog(products) });
    return products.some(function (p) { return engine.completes(p, rules, data, chem, a, o); });
  };

  // For a question: { answer id: true when it can still lead to a pump }. null when the question is never greyed out
  // (the chemical search and the flammability question, which must be answered truthfully) or when no answer can lead to
  // a pump (the wizard then carries on to the contact form as before).
  engine.available = function (products, rules, data, a, step) {
    if (step === 'chemical' || step === 'flammable') return null;
    var o = { catalog: PE.partsCatalog(products) }, out = {}, any = false;
    engine.optionsFor(rules, a, step).forEach(function (x) {
      var b = Object.assign({}, a); b[step] = step === 'extras' ? [x.id] : x.id;
      out[x.id] = engine.reachable(products, rules, data, b, o);
      any = any || out[x.id];
    });
    return any ? out : null;
  };

  engine.summary = function (rules, data, a) {
    var parts = [];
    Object.keys(a).forEach(function (k) {
      var q = rules.questions[k];
      if (!q) return;
      var v = a[k];
      if (k === 'chemical') {
        var c = engine.lookupChemical(PE.chemicals(data), v);
        parts.push('Chemical: ' + (c ? c.n + (c.cas ? ' (CAS ' + c.cas + ')' : '') : 'not listed'));
        return;
      }
      if (Array.isArray(v)) { parts.push(q.title + ' ' + v.map(function (id) { var o = optionById(q, id); return o ? o.label : id; }).join(', ')); return; }
      var o = optionById(q, v);
      parts.push(q.title + ' ' + (o ? o.label : v));
    });
    return parts.join(' | ');
  };

  // Answers carried in the page URL (the homepage hero links here with ?fluid=…): only the first question, and only
  // an answer the rules know. Anything else is ignored and the wizard starts at question 1.
  engine.answersFromUrl = function (rules, search) {
    var id = new URLSearchParams(search || '').get('fluid'), out = {};
    if (id && (rules.questions.fluid.options || []).some(function (o) { return o.id === id; })) out.fluid = id;
    return out;
  };

  // ---------------------------------------------------------------- UI
  function el(tag, attrs, children) {
    var e = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === 'class') e.className = attrs[k];
      else if (k.indexOf('on') === 0) e.addEventListener(k.slice(2), attrs[k]);
      else e.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { if (c) e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return e;
  }
  function money(v) { return '$' + Number(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  // The flame mark next to a flammable or combustible chemical in the list (same icon as Shop by Chemical)
  var FLAME_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>';
  function flameFlag(label) { var s = el('span', { class: 'fmp-flag' }, [label]); s.insertAdjacentHTML('afterbegin', FLAME_SVG); return s; }
  // ?src=… on the page URL (the homepage hero adds src=home) goes out with every event, so entry points can be compared
  var SRC = (function () { try { return new URLSearchParams(window.location.search).get('src') || ''; } catch (e) { return ''; } })();
  function track(step, answer) {
    try {
      if (!window.dataLayer) return;
      var ev = { event: 'fmp_step', fmp_step: step, fmp_answer: String(answer) };
      if (SRC) ev.fmp_src = SRC;
      window.dataLayer.push(ev);
    } catch (e) {}
  }
  // Shopify.routes.root is "/" or a locale/market prefix such as "/en-ca/"; storefront URLs must be built from it.
  function shopRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  function fetchAll(root, handle) {
    var direct = root.getAttribute('data-products-url');
    if (direct) return fetch(direct).then(function (r) { return r.json(); }).then(function (j) { return j.products || j; });
    function page(n) {
      return fetch(shopRoot() + 'collections/' + encodeURIComponent(handle) + '?view=fmp-json&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (t) { return JSON.parse(t); });
    }
    return page(1).then(function (first) {
      var rest = [];
      for (var n = 2; n <= (first.pages || 1); n++) rest.push(page(n));
      return Promise.all(rest).then(function (others) {
        return others.reduce(function (acc, o) { return acc.concat(o.products || []); }, first.products || []);
      });
    });
  }

  function mount(root) {
    if (root.__fmp) return;
    root.__fmp = true;
    var handle = root.getAttribute('data-collection') || 'find-my-pump';
    var contactUrl = root.getAttribute('data-contact') || '/pages/contact';
    var state = { answers: {}, history: [] };
    var rules, data, chems, products;

    root.innerHTML = '<p class="fmp-loading">Loading…</p>';
    Promise.all([
      PE ? Promise.resolve() : Promise.reject(new Error('pump-engine.js is not loaded')),
      fetch(root.getAttribute('data-rules')).then(function (r) { return r.json(); }),
      fetch(root.getAttribute('data-chemicals')).then(function (r) { return r.json(); }),
      fetchAll(root, handle)
    ]).then(function (res) {
      rules = res[1]; data = res[2]; chems = PE.chemicals(data); products = res[3];
      if (rules.contact_url && !root.getAttribute('data-contact')) contactUrl = rules.contact_url;
      // arriving from the homepage hero with the first answer: start at question 2, with Back leading to question 1.
      // An answer with no pump in the range (greyed out on question 1) starts at question 1 instead.
      var pre = engine.answersFromUrl(rules, window.location.search);
      if (pre.fluid && engine.reachable(products, rules, data, pre)) { state.history.push({}); state.answers = pre; }
      render();
    }).catch(function (err) {
      root.innerHTML = '';
      root.appendChild(el('p', { class: 'fmp-error' }, ['Sorry, the pump finder could not load. ', el('a', { href: contactUrl }, ['Contact us']), ' and we will help you choose.']));
      if (window.console) console.error('Find My Pump failed to load', err);
    });

    var ORDER = ['fluid', 'fuel_type', 'chemical', 'flammable', 'container', 'thickness', 'volume', 'power', 'extras'];

    function answer(step, value) {
      state.history.push(JSON.parse(JSON.stringify(state.answers)));
      state.answers[step] = value;
      track(step, step === 'chemical' ? chemicalName(value) : value);
      render();
    }
    function chemicalName(id) { var c = engine.lookupChemical(chems, id); return c ? c.n : 'not listed'; }
    function back() {
      if (state.history.length) { state.answers = state.history.pop(); render(); }
    }
    function restart() { state.answers = {}; state.history = []; render(); }

    function render() {
      var a = state.answers;
      var step = engine.nextStep(rules, data, a);
      root.innerHTML = '';
      if (step) return renderQuestion(step);
      var out = engine.evaluate(products, rules, data, a);
      if (out.status === 'ok') return renderResult(out);
      return renderContact(out);
    }

    function progress(step) {
      var idx = ORDER.indexOf(step), total = ORDER.length;
      return el('div', { class: 'fmp-progress', role: 'progressbar', 'aria-valuenow': idx + 1, 'aria-valuemax': total }, [
        el('span', { class: 'fmp-progress-bar', style: 'width:' + Math.round(((idx + 1) / total) * 100) + '%' })
      ]);
    }

    function header(step, q) {
      return el('div', { class: 'fmp-head' }, [
        progress(step),
        state.history.length ? el('button', { class: 'fmp-back', type: 'button', onclick: back }, ['← Back']) : null,
        el('h2', { class: 'fmp-title' }, [q.title]),
        q.optional ? el('p', { class: 'fmp-hint' }, ['Optional — pick any that apply.']) : null
      ]);
    }

    function renderQuestion(step) {
      var q = rules.questions[step];
      var a = state.answers;
      var wrap = el('div', { class: 'fmp-step fmp-step-' + step }, [header(step, q)]);

      // Every chemical A–Z in a scrolling list that narrows as you type (PumpEngine.browse); rows are added as it scrolls
      if (step === 'chemical') {
        var PAGE = 60, items = [], shown = 0;
        var input = el('input', { class: 'form-field-input fmp-search', type: 'search', placeholder: 'Chemical name, common name or CAS number…', autocomplete: 'off', 'aria-label': q.title, 'aria-controls': 'fmp-chem-list' });
        var note = el('p', { class: 'fmp-list-note', 'aria-live': 'polite' });
        var list = el('ul', { class: 'fmp-results fmp-chem-list', id: 'fmp-chem-list' });
        var row = function (c) {
          var fire = PE.flameLabel(c);
          return el('li', {}, [el('button', { type: 'button', class: 'fmp-opt fmp-opt-inline', onclick: function () { answer('chemical', c.id); } }, [
            el('span', { class: 'fmp-opt-label' }, [c.n]),
            c.cas || fire ? el('span', { class: 'fmp-opt-hint' }, [c.cas ? 'CAS ' + c.cas : null, fire ? flameFlag(fire) : null]) : null
          ])]);
        };
        var more = function () { for (var end = Math.min(items.length, shown + PAGE); shown < end; shown++) list.appendChild(row(items[shown])); };
        var fill = function () {
          var t = input.value.trim();
          items = PE.browse(chems, t); shown = 0; list.innerHTML = ''; list.scrollTop = 0;
          note.textContent = !t ? PE.browseNote(items.length)
            : items.length ? items.length.toLocaleString('en-US') + (items.length === 1 ? ' match.' : ' matches.')
            : 'No match. Try another name or the CAS number, or choose “' + q.not_listed_label + '” below.';
          list.hidden = !items.length;
          more();
        };
        list.addEventListener('scroll', function () { if (shown < items.length && list.scrollTop + list.clientHeight > list.scrollHeight - 200) more(); });
        input.addEventListener('input', fill);
        wrap.appendChild(input); wrap.appendChild(note); wrap.appendChild(list);
        wrap.appendChild(el('button', { type: 'button', class: 'fmp-link', onclick: function () { answer('chemical', NOT_LISTED); } }, [q.not_listed_label]));
        root.appendChild(wrap);
        fill();
        input.focus();
        return;
      }

      // Answers that can no longer lead to a pump are greyed out and can't be picked (never the flammability question)
      var options = engine.optionsFor(rules, a, step);
      var avail = engine.available(products, rules, data, a, step);
      var noteId = 'fmp-unavailable-' + step;
      var dead = function (o) { return !!avail && !avail[o.id]; };
      var grid = el('div', { class: 'fmp-options' });
      var optButton = function (o, attrs, onclick) {
        var b = el('button', Object.assign({ type: 'button', class: 'fmp-opt' + (dead(o) ? ' is-unavailable' : ''), 'data-id': o.id }, attrs), [
          el('span', { class: 'fmp-opt-label' }, [o.label]), o.hint ? el('span', { class: 'fmp-opt-hint' }, [o.hint]) : null
        ]);
        if (dead(o)) { b.setAttribute('aria-disabled', 'true'); b.setAttribute('aria-describedby', noteId); } else b.addEventListener('click', onclick);
        return b;
      };
      var unavailableNote = function () {
        if (!options.some(dead)) return null;
        return el('p', { class: 'fmp-unavailable-note', id: noteId }, [
          Object.keys(a).length ? 'Greyed-out answers have no pump that fits your earlier answers. Need one anyway? ' : 'Greyed-out answers have no pump in our range yet. Need one? ',
          el('a', { href: contactLink(), onclick: function () { track(step + '_unavailable_contact', options.filter(dead).map(function (o) { return o.id; }).join(',')); } }, ['Contact us']), '.'
        ]);
      };

      if (q.multi) {
        var chosen = [];
        options.forEach(function (o) {
          grid.appendChild(optButton(o, { 'aria-pressed': 'false' }, function () {
            if (o.id === 'none') { chosen = ['none']; } else { chosen = chosen.filter(function (x) { return x !== 'none'; }); var i = chosen.indexOf(o.id); if (i >= 0) chosen.splice(i, 1); else chosen.push(o.id); }
            Array.prototype.forEach.call(grid.children, function (c) { c.setAttribute('aria-pressed', chosen.indexOf(c.getAttribute('data-id')) >= 0 ? 'true' : 'false'); });
          }));
        });
        wrap.appendChild(grid);
        var n1 = unavailableNote(); if (n1) wrap.appendChild(n1);
        wrap.appendChild(el('button', { type: 'button', class: 'button-primary fmp-primary', onclick: function () { answer('extras', chosen.length ? chosen : ['none']); } }, ['Show my pump']));
        root.appendChild(wrap);
        return;
      }

      options.forEach(function (o) { grid.appendChild(optButton(o, {}, function () { answer(step, o.id); })); });
      wrap.appendChild(grid);
      var n2 = unavailableNote(); if (n2) wrap.appendChild(n2);
      root.appendChild(wrap);
    }

    // Adds a pump and its upgrade in one request, then opens the cart
    // "Add both to cart": the pump and its upgrade parts together, then the cart
    function addTogether(p, parts, btn) {
      var old = btn.parentNode.querySelector('.fmp-cart-error');
      if (old) old.remove();
      btn.disabled = true; var label = btn.textContent; btn.textContent = 'Adding…';
      PE.addTogether(shopRoot(), p.variant_id, parts.map(function (part) { return part.product.variant_id; })).then(function (res) {
        var message = PE.cartMessage(res), cart = root.getAttribute('data-cart-url') || (shopRoot() + 'cart');
        if (res.ok) track('add_upgrade_to_cart', p.handle);
        if (res.ok && !message) { window.location.href = cart; return; }
        var note = el('p', { class: 'fmp-cart-error', role: 'alert' }, [message, ' ', el('a', { href: cart }, ['View cart'])]);
        if (btn.isConnected) {
          btn.disabled = res.ok; btn.textContent = res.ok ? 'Added' : label;
          btn.insertAdjacentElement('afterend', note);
        } else {
          note.firstChild.textContent = p.title + ': ' + message;
          root.insertBefore(note, root.firstChild);  // the customer moved on while adding: keep the message in view
        }
      });
    }

    function card(p, primary, upgrade) {
      var fluids = arr(p.fluid_tags).join(', ');
      var chem = chemicalBranch(state.answers) ? engine.lookupChemical(chems, state.answers.chemical) : null;
      var rating = chem ? PE.rate(p, chem, data, state.answers.flammable, PE.partsCatalog(products)) : null;
      var hoseNote = (chem && arr(chem.h).length && !PE.bundledHose(p)) ? 'Use ' + chem.h.join(' or ') + ' hose with this liquid.' : null;
      var meta = rating ? 'Rated ' + rating.rating + ' for ' + chem.n + ' by ' + rating.label + (upgrade ? ' with the upgrade below' : '') : (fluids ? 'Rated for: ' + fluids : null);
      var parts = upgrade ? upgrade.parts : [], total = p.price + parts.reduce(function (t, part) { return t + Number(part.product.price || 0); }, 0);
      return el('div', { class: 'fmp-card' + (primary ? ' fmp-card-primary' : '') }, [
        p.image ? el('img', { class: 'fmp-card-img', src: p.image, alt: p.title, loading: 'lazy' }) : null,
        el('div', { class: 'fmp-card-body' }, [
          el('h3', { class: 'fmp-card-title' }, [p.title]),
          el('p', { class: 'fmp-card-price' }, [money(total) + (parts.length ? ' with upgrade' : '')]),
          el('p', { class: 'fmp-card-meta' }, [[meta, p.flow, p.power].filter(Boolean).join(' · ')]),
          hoseNote ? el('p', { class: 'fmp-card-meta' }, [hoseNote]) : null,
          // for a flammable liquid, say whether this pump arrives ready to ground or the customer grounds it.
          // The gasoline branch asks no chemical, so the fluid tag decides there.
          (chem ? PE.needsGroundable(chem, state.answers.flammable) : engine.fluidTag(rules, state.answers) === 'gasoline')
            && (rules.grounding || {})[PE.grounding(p) + '_note']
            ? el('p', { class: 'fmp-card-ground' }, [rules.grounding[PE.grounding(p) + '_note']]) : null,
          rating && rating.warnings.length ? el('ul', { class: 'fmp-warn' }, rating.warnings.map(function (t) { return el('li', {}, [rating.label + ' warning: ' + t]); })) : null,
          parts.length ? el('div', { class: 'fmp-upgrade' }, [
            el('p', { class: 'fmp-upgrade-head' }, ['Required upgrade']),
            el('ul', {}, parts.map(function (part) { return el('li', {}, [el('a', { href: part.product.url }, [part.label]), ' · ' + money(part.product.price) + (part.note ? '. ' + part.note : '')]); })),
            el('p', { class: 'fmp-card-meta' }, [upgrade.reasons.join(' ')])
          ]) : null,
          p.variant_id && parts.length ? el('button', { type: 'button', class: 'button-primary fmp-primary', onclick: function (e) { addTogether(p, parts, e.currentTarget); } }, [parts.length === 1 ? 'Add both to cart' : 'Add all to cart']) : null,
          primary && p.variant_id && !parts.length ? el('form', { method: 'post', action: shopRoot() + 'cart/add', class: 'fmp-cart' }, [
            el('input', { type: 'hidden', name: 'id', value: p.variant_id }),
            el('button', { type: 'submit', class: 'button-primary fmp-primary' }, ['Add to cart'])
          ]) : null,
          el('a', { class: 'fmp-link', href: p.url }, ['View product'])
        ])
      ]);
    }

    function renderResult(out) {
      track(out.upgrade ? 'result_with_upgrade' : 'result', out.result.handle);
      var wrap = el('div', { class: 'fmp-result' }, [
        el('p', { class: 'fmp-eyebrow' }, [out.upgrade ? 'Your pump, with the upgrade it needs' : 'Your pump']),
        card(out.result, true, out.upgrades[out.result.handle])
      ]);
      if (out.alternates.length) {
        var det = el('details', { class: 'fmp-alternates' }, [el('summary', {}, ['Not quite right? See ' + out.alternates.length + ' alternative' + (out.alternates.length > 1 ? 's' : '')])]);
        out.alternates.forEach(function (p) { det.appendChild(card(p, false, out.upgrades[p.handle])); });
        wrap.appendChild(det);
      }
      wrap.appendChild(el('p', { class: 'fmp-footnote' }, ['Ratings come from the manufacturer. Not sure? ', el('a', { href: contactLink() }, ['Talk to us']), '.']));
      // Back first: from a result most people want to change one answer (a different drum size, say), not start again.
      wrap.appendChild(el('button', { type: 'button', class: 'fmp-link', onclick: back }, ['← Change my answers']));
      wrap.appendChild(el('button', { type: 'button', class: 'fmp-link fmp-link-sep', onclick: restart }, ['Start over']));
      root.appendChild(wrap);
    }

    function contactLink() {
      return contactUrl + (contactUrl.indexOf('?') >= 0 ? '&' : '?') + 'fmp=' + encodeURIComponent(engine.summary(rules, data, state.answers));
    }

    function renderContact(out) {
      track('no_match', out.reason || (out.checks && out.checks.length ? 'check_with_us' : out.status));
      var checks = arr(out.checks);
      var msg = out.reason === 'chemical'
        ? 'We do not have a manufacturer rating for that liquid, so we will not guess. Tell us what you are pumping and we will recommend the right pump.'
        : checks.length
          ? 'Before we recommend a pump for this liquid, here is what the manufacturer says:'
          : 'We could not match a pump to those answers with confidence. Tell us a little more and we will recommend one.';
      root.appendChild(el('div', { class: 'fmp-contact' }, [
        el('p', { class: 'fmp-eyebrow' }, ['Let us pick it for you']),
        el('p', {}, [msg]),
        checks.length ? el('ul', { class: 'fmp-checks' }, checks.map(function (t) { return el('li', {}, [t]); })) : null,
        el('a', { class: 'button-primary fmp-primary', href: contactLink() }, ['Send us your answers']),
        el('button', { type: 'button', class: 'fmp-link ml-5', onclick: back }, ['← Change my answers'])
      ]));
    }
  }

  var FMP = { engine: engine, mount: mount };
  if (inNode) module.exports = FMP;
  else if (!window.FMP) {
    window.FMP = FMP;
    var boot = function (scope) { Array.prototype.forEach.call((scope || document).querySelectorAll('[data-fmp]'), mount); };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', function () { boot(); }) : boot();
    // Theme editor: sections are re-rendered in place without a page load
    document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  }
})();
